/**
 * ShareButton Component
 *
 * Share URLs button for copying shareable entity URLs to clipboard.
 * Selection-dependent - requires at least one row selected.
 *
 * Converted from Dropdown to true Button for better UX (1 click instead of 2).
 *
 * Usage:
 * ```tsx
 * <ShareButton onClick={() => handleShare(selectedAssets)} />
 * ```
 *
 * @module components/actions
 */

import { ShareIcon } from '@heroicons/react/24/outline';

import { Button } from '@/components/Button';

export interface ShareButtonProps {
  /** Handler for share button click */
  onClick: () => void;
  /** Disable the button when no assets selected */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Test ID for E2E testing */
  'data-testid'?: string;
}

/**
 * ShareButton Component
 *
 * Selection-dependent share button that matches the visual style of action bar.
 * Used across multiple tables (Assets, Vulnerabilities, Technologies, Seeds).
 *
 * Now uses true Button component for direct 1-click interaction.
 *
 * @param props - Component props
 * @returns Share button JSX
 *
 * @example
 * ```tsx
 * <ShareButton
 *   onClick={() => handlers.handleShare(assets)}
 *   disabled={!hasSelection}
 *   data-testid="asset-share-button"
 * />
 * ```
 */
export function ShareButton({
  onClick,
  disabled = false,
  className,
  'data-testid': testId = 'share-button',
}: ShareButtonProps) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      styleType="rounded"
      size="xxs"
      startIcon={<ShareIcon className="size-4" />}
      className={className}
      data-testid={testId}
      aria-label="Share URLs"
    >
      Share
    </Button>
  );
}
